import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { ProductForm } from '@/components/seller/product-form'
import { ProductCard } from '@/components/seller/product-card'
import { PaymentSettings } from '@/components/seller/payment-settings'
import { SellerPaymentHistory } from './payment-history'
import { SellerRevenuePage } from './revenue'
import { SellerOrdersPage } from './orders'
import { LoanRequestForm } from '@/components/seller/loan-request-form'
import { LoanHistory } from '@/components/seller/loan-history'
import { Toast } from '@/components/ui/toast'
import { SubscriptionModal } from '@/components/subscription/SubscriptionModal'
import {
  Package,
  DollarSign,
  ShoppingBag,
  Truck,
  Video,
  Settings,
  Plus,
  Search,
  Star,
  BarChart,
  Clock,
  CheckCircle,
  XCircle,
  MapPin,
  Eye,
  User,
  CreditCard,
  Info,
  Crown,
  ArrowRight,
  AlertCircle,
  Store,
  Edit
} from 'lucide-react'

interface SellerStore {
  id: string;
  name: string;
  description: string | null;
  subscription_type: string;
  monthly_product_count: number;
  total_sales: number;
  total_revenue: number;
  rating: number;
  status: string;
}

interface DeliveryRequest {
  id: string;
  order_id: string;
  current_step: string;
  pickup_instructions: string;
  delivery_notes: string;
  created_at: string;
  step_history?: Array<{
    step: string;
    timestamp: string;
  }>;
  step_metadata: any;
  order: {
    id: string;
    delivery_type: string;
    delivery_address: string;
    final_price: number;
    product: {
      title: string;
    };
    buyer: {
      full_name: string | null;
      phone_number: string | null;
    };
  };
  delivery_partner?: {
    applicant_name: string;
    applicant_email: string;
    vehicle_type: string;
  } | null;
}

const DELIVERY_STEPS = [
  { value: 'pending', label: 'En attente', icon: Clock, color: 'text-yellow-500' },
  { value: 'accepted', label: 'Acceptée', icon: CheckCircle, color: 'text-blue-500' },
  { value: 'preparing', label: 'En préparation', icon: Package, color: 'text-purple-500' },
  { value: 'pickup_ready', label: 'Prêt pour récupération', icon: Package, color: 'text-indigo-500' },
  { value: 'picked_up', label: 'Récupéré', icon: Truck, color: 'text-orange-500' },
  { value: 'in_transit', label: 'En transit', icon: Truck, color: 'text-blue-500' },
  { value: 'arrived', label: 'Arrivé', icon: MapPin, color: 'text-green-500' },
  { value: 'delivered', label: 'Livré', icon: CheckCircle, color: 'text-green-500' },
  { value: 'cancelled', label: 'Annulé', icon: XCircle, color: 'text-red-500' }
]

// Product limits based on subscription type
const PRODUCT_LIMITS = {
  free: 8,
  premium: 25
}

function SellerDashboard() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [activeTab, setActiveTab] = useState('products')
  const [showProductModal, setShowProductModal] = useState(false)
  const [showLoanRequestModal, setShowLoanRequestModal] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [productsLoading, setProductsLoading] = useState(true)
  const [products, setProducts] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [sellerStore, setSellerStore] = useState<SellerStore | null>(null)
  const [loading, setLoading] = useState(true)
  const [deliveryRequests, setDeliveryRequests] = useState<DeliveryRequest[]>([])
  const [showDeliveryDetails, setShowDeliveryDetails] = useState(false)
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryRequest | null>(null)
  const [pickupLocation, setPickupLocation] = useState('')
  const [pickupInstructions, setPickupInstructions] = useState('')
  const [showPickupLocationModal, setShowPickupLocationModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<any>(null) // For editing
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false) // For delete confirmation
  const [productToDelete, setProductToDelete] = useState<any>(null) // For delete confirmation

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    const checkSellerStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('seller_stores')
          .select('*')
          .eq('seller_id', user!.id)
          .single()

        if (error) throw error
        if (!data) {
          navigate('/seller/register')
          return
        }

        if (data.status !== 'approved') {
          navigate('/seller/application-status')
          return
        }

        setSellerStore(data)
      } catch (err) {
        console.error('Error checking seller status:', err)
        navigate('/seller/register')
      } finally {
        setLoading(false)
      }
    }

    checkSellerStatus()
  }, [user, navigate])

  useEffect(() => {
    if (!loading && user && sellerStore?.status === 'approved') {
      fetchProducts()
      fetchDeliveryRequests()
    }
  }, [user, loading, sellerStore])

  const fetchProducts = async () => {
    if (!user) return

    try {
      setProductsLoading(true)
      let query = supabase
        .from('products')
        .select('*')
        .eq('seller_id', user.id)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
      
      // Apply search filter if provided
      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`)
      }

      const { data, error } = await query

      if (error) throw error
      setProducts(data || [])
    } catch (err) {
      console.error('Error fetching products:', err)
    } finally {
      setProductsLoading(false)
    }
  }

  const fetchDeliveryRequests = async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('delivery_requests')
        .select(`
          *,
          order:orders (
            id,
            delivery_type,
            delivery_address,
            final_price,
            product:products (
              title
            ),
            buyer:profiles!buyer_id (
              full_name,
              phone_number
            )
          ),
          delivery_partner:delivery_partners (
            applicant_name,
            applicant_email,
            vehicle_type
          )
        `)
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setDeliveryRequests(data || [])
    } catch (err) {
      console.error('Error fetching delivery requests:', err)
    }
  }

  const handleProductSuccess = () => {
    fetchProducts()
    setShowProductModal(false)
    setSelectedProduct(null) // Reset selected product after success
  }

  const handleLoanRequestSuccess = () => {
    setShowLoanRequestModal(false)
    if (activeTab !== 'loans') {
      setActiveTab('loans')
    }
  }

  const handleEditProduct = (product: any) => {
    setSelectedProduct(product)
    setShowProductModal(true)
  }

  const handleDeleteProduct = (product: any) => {
    setProductToDelete(product)
    setShowDeleteConfirmModal(true)
  }

  const confirmDeleteProduct = async () => {
    if (!productToDelete || !user) return

    try {
      setLoading(true)
      
      // Instead of deleting the product, mark it as deleted (soft delete)
      const { error } = await supabase
        .from('products')
        .update({ is_deleted: true })
        .eq('id', productToDelete.id)
        .eq('seller_id', user.id)

      if (error) throw error

      setToastMessage('Produit supprimé avec succès')
      setToastType('success')
      setShowToast(true)
      fetchProducts()
    } catch (err) {
      console.error('Error deleting product:', err)
      setToastMessage('Erreur lors de la suppression du produit')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
      setShowDeleteConfirmModal(false)
      setProductToDelete(null)
    }
  }

  const handleDeliveryAction = async (deliveryId: string, action: 'preparing' | 'pickup_ready') => {
    try {
      if (action === 'pickup_ready' && !pickupLocation) {
        setSelectedDelivery(deliveryRequests.find(d => d.id === deliveryId) || null)
        setShowPickupLocationModal(true)
        return
      }

      const updateData: any = { 
        current_step: action,
        step_metadata: {
          updated_by: user?.id,
          updated_at: new Date().toISOString()
        }
      }

      // Add pickup location and instructions if provided
      if (action === 'pickup_ready' && pickupLocation) {
        updateData.pickup_instructions = `Adresse de récupération: ${pickupLocation}${pickupInstructions ? `\n\nInstructions: ${pickupInstructions}` : ''}`
      }

      const { error } = await supabase
        .from('delivery_requests')
        .update(updateData)
        .eq('id', deliveryId)

      if (error) throw error

      setToastMessage(`Étape mise à jour avec succès`)
      setToastType('success')
      setShowToast(true)
      setShowDeliveryDetails(false)
      setShowPickupLocationModal(false)
      setPickupLocation('')
      setPickupInstructions('')
      fetchDeliveryRequests()
    } catch (err) {
      console.error('Error updating delivery step:', err)
      setToastMessage('Erreur lors de la mise à jour de l\'étape')
      setToastType('error')
      setShowToast(true)
    }
  }

  const handleSubscriptionSuccess = async () => {
    try {
      // Refresh seller store data
      const { data, error } = await supabase
        .from('seller_stores')
        .select('*')
        .eq('seller_id', user?.id)
        .single()

      if (error) throw error
      
      setSellerStore(data)
      setShowSubscriptionModal(false)
      setToastMessage('Abonnement mis à niveau avec succès')
      setToastType('success')
      setShowToast(true)
    } catch (err) {
      console.error('Error refreshing seller data:', err)
    }
  }

  const canAddProduct = () => {
    if (!sellerStore) return false
    
    const limit = sellerStore.subscription_type === 'premium' 
      ? PRODUCT_LIMITS.premium 
      : PRODUCT_LIMITS.free
      
    return products.length < limit
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {showProductModal && (
        <ProductForm
          onClose={() => {
            setShowProductModal(false)
            setSelectedProduct(null) // Reset selected product when closing
          }}
          onSuccess={handleProductSuccess}
          product={selectedProduct} // Pass selected product for editing
        />
      )}

      {showLoanRequestModal && (
        <LoanRequestForm
          onClose={() => setShowLoanRequestModal(false)}
          onSuccess={handleLoanRequestSuccess}
        />
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && sellerStore && (
        <SubscriptionModal
          onClose={() => setShowSubscriptionModal(false)}
          onSuccess={handleSubscriptionSuccess}
          storeId={sellerStore.id}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && productToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <AlertCircle className="h-12 w-12 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirmer la suppression
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Êtes-vous sûr de vouloir supprimer le produit "{productToDelete.title}" ? Cette action est irréversible.
              </p>
              <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowDeleteConfirmModal(false)
                    setProductToDelete(null)
                  }}
                  className="w-full sm:w-auto"
                >
                  Annuler
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 w-full sm:w-auto"
                  onClick={confirmDeleteProduct}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Supprimer'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pickup Location Modal */}
      {showPickupLocationModal && selectedDelivery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Adresse de récupération
                </h3>
                <button
                  onClick={() => setShowPickupLocationModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Veuillez fournir l'adresse où le livreur peut récupérer le colis.
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="pickupLocation" className="block text-sm font-medium text-gray-700">
                    Adresse de récupération *
                  </label>
                  <textarea
                    id="pickupLocation"
                    rows={3}
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Entrez l'adresse complète où le livreur peut récupérer le colis"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="pickupInstructions" className="block text-sm font-medium text-gray-700">
                    Instructions supplémentaires (optionnel)
                  </label>
                  <textarea
                    id="pickupInstructions"
                    rows={3}
                    value={pickupInstructions}
                    onChange={(e) => setPickupInstructions(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Informations supplémentaires pour le livreur (code d'entrée, étage, personne à contacter, etc.)"
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowPickupLocationModal(false)}
                  className="w-full sm:w-auto"
                >
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (selectedDelivery && pickupLocation) {
                      handleDeliveryAction(selectedDelivery.id, 'pickup_ready')
                    }
                  }}
                  disabled={!pickupLocation}
                  className="w-full sm:w-auto"
                >
                  Confirmer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Details Modal */}
      {showDeliveryDetails && selectedDelivery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">
                  Détails de la Livraison
                </h3>
                <button
                  onClick={() => setShowDeliveryDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-6 space-y-6">
                {/* Delivery Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Informations</h4>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-900">ID: #{selectedDelivery.order_id.slice(0, 8)}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(selectedDelivery.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedDelivery.current_step === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : selectedDelivery.current_step === 'delivered'
                          ? 'bg-green-100 text-green-800'
                          : selectedDelivery.current_step === 'cancelled'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {DELIVERY_STEPS.find(s => s.value === selectedDelivery.current_step)?.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Product Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Produit</h4>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {selectedDelivery.order.product.title}
                    </p>
                    <p className="text-sm text-gray-900">
                      Prix: ${selectedDelivery.order.final_price}
                    </p>
                  </div>
                </div>

                {/* Delivery Partner */}
                {selectedDelivery.delivery_partner && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Livreur</h4>
                    <div className="mt-2">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-4 w-4 text-gray-500" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {selectedDelivery.delivery_partner.applicant_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {selectedDelivery.delivery_partner.applicant_email}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pickup Instructions */}
                {selectedDelivery.pickup_instructions && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Instructions de récupération</h4>
                    <div className="mt-2 p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700 whitespace-pre-line">
                        {selectedDelivery.pickup_instructions}
                      </p>
                    </div>
                  </div>
                )}

                {/* Delivery Steps */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Étapes de la Livraison</h4>
                  <div className="mt-4 relative">
                    <div className="absolute top-0 left-4 h-full w-0.5 bg-gray-200"></div>
                    <div className="space-y-6">
                      {DELIVERY_STEPS.map((step, index) => {
                        const isCompleted = selectedDelivery.step_history?.some(h => h.step === step.value)
                        const isCurrent = selectedDelivery.current_step === step.value
                        const stepData = selectedDelivery.step_history?.find(h => h.step === step.value)

                        return (
                          <div
                            key={step.value}
                            className={`relative flex items-start ${
                              isCompleted ? 'opacity-100' : 'opacity-50'
                            }`}
                          >
                            <div className={`absolute left-0 h-8 w-8 rounded-full bg-white border-2 flex items-center justify-center ${
                              isCompleted 
                                ? 'border-primary' 
                                : isCurrent
                                  ? 'border-primary'
                                  : 'border-gray-200'
                            }`}>
                              <step.icon className={`h-4 w-4 ${step.color}`} />
                            </div>
                            <div className="ml-12">
                              <p className="text-sm font-medium text-gray-900">
                                {step.label}
                              </p>
                              {stepData && (
                                <p className="mt-1 text-xs text-gray-500">
                                  {new Date(stepData.timestamp).toLocaleString()}
                                </p>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-6 border-t flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowDeliveryDetails(false)}
                    className="w-full sm:w-auto"
                  >
                    Fermer
                  </Button>
                  {selectedDelivery.current_step === 'accepted' && (
                    <Button
                      variant="primary"
                      onClick={() => handleDeliveryAction(selectedDelivery.id, 'preparing')}
                      className="w-full sm:w-auto"
                    >
                      <Package className="h-4 w-4 mr-2" />
                      Commencer la Préparation
                    </Button>
                  )}
                  {selectedDelivery.current_step === 'preparing' && (
                    <Button
                      variant="primary"
                      onClick={() => handleDeliveryAction(selectedDelivery.id, 'pickup_ready')}
                      className="w-full sm:w-auto"
                    >
                      <Package className="h-4 w-4 mr-2" />
                      Prêt pour la Récupération
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Store Status Banner */}
        <div className="mb-8 bg-gradient-to-r from-primary to-blue-600 text-white rounded-lg shadow-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <div className="flex items-center">
                <h2 className="text-xl sm:text-2xl font-bold">{sellerStore?.name || 'My Store'}</h2>
                <Link to="/seller/store-profile" className="ml-3 bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-md text-sm flex items-center transition-colors">
                  <Store className="h-4 w-4 mr-2" />
                  Profil de la Boutique
                </Link>
              </div>
              <p className="mt-1 text-white/80 text-sm">{sellerStore?.description}</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-white/80">Abonnement</div>
              <div className="text-lg font-semibold capitalize flex items-center">
                {sellerStore?.subscription_type === 'premium' ? (
                  <>
                    <Crown className="h-5 w-5 text-yellow-300 mr-1" />
                    Premium
                  </>
                ) : (
                  <>
                    Gratuit
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="ml-2 text-white border-white hover:bg-white/20"
                      onClick={() => setShowSubscriptionModal(true)}
                    >
                      <Crown className="h-4 w-4 text-yellow-300 mr-1" />
                      Passer à Premium
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            <div className="bg-white/10 rounded-lg p-3 sm:p-4">
              <div className="text-xs sm:text-sm text-white/80">Produits</div>
              <div className="mt-1 text-lg sm:text-2xl font-bold">
                {products.length}
                <span className="text-xs sm:text-sm text-white/60 ml-1">
                  / {sellerStore?.subscription_type === 'premium' ? PRODUCT_LIMITS.premium : PRODUCT_LIMITS.free}
                </span>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 sm:p-4">
              <div className="text-xs sm:text-sm text-white/80">Ventes Totales</div>
              <div className="mt-1 text-lg sm:text-2xl font-bold">
                {sellerStore?.total_sales || 0}
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 sm:p-4">
              <div className="text-xs sm:text-sm text-white/80">Revenus Totaux</div>
              <div className="mt-1 text-lg sm:text-2xl font-bold">
                ${sellerStore?.total_revenue?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-3 sm:p-4">
              <div className="text-xs sm:text-sm text-white/80">Note Moyenne</div>
              <div className="mt-1 flex items-center">
                <span className="text-lg sm:text-2xl font-bold mr-2">
                  {sellerStore?.rating?.toFixed(1) || '0.0'}
                </span>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 sm:h-5 sm:w-5 ${
                        i < Math.floor(sellerStore?.rating || 0)
                          ? 'text-yellow-400 fill-current'
                          : 'text-white/30'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200">
            <div className="overflow-x-auto">
              <nav className="-mb-px flex space-x-2 sm:space-x-4 p-1">
                {[
                  { id: 'products', label: 'Produits', icon: Package },
                  { id: 'orders', label: 'Commandes', icon: ShoppingBag },
                  { id: 'delivery', label: 'Livraisons', icon: Truck },
                  { id: 'videos', label: 'Vidéos', icon: Video },
                  { id: 'revenue', label: 'Revenus', icon: BarChart },
                  { id: 'payments', label: 'Paiements', icon: DollarSign },
                  { id: 'loans', label: 'Prêts', icon: CreditCard },
                  { id: 'settings', label: 'Paramètres', icon: Settings }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } flex items-center px-2 sm:px-4 py-3 sm:py-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap`}
                  >
                    <tab.icon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                    <span className="hidden xs:inline">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6">
            {activeTab === 'products' && (
              <div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                  <div className="flex-1 max-w-lg w-full">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                        placeholder="Rechercher un produit..."
                      />
                    </div>
                  </div>
                  <div>
                    {canAddProduct() ? (
                      <Button
                        variant="primary"
                        onClick={() => {
                          setSelectedProduct(null) // Ensure no product is selected for editing
                          setShowProductModal(true)
                        }}
                        className="w-full sm:w-auto"
                      >
                        <Plus className="h-5 w-5 mr-2" />
                        Ajouter un Produit
                      </Button>
                    ) : (
                      <div className="flex flex-col sm:flex-row items-center gap-2">
                        <div className="text-sm text-red-600 bg-red-50 px-3 py-1 rounded-md">
                          Limite de produits atteinte
                        </div>
                        {sellerStore?.subscription_type !== 'premium' && (
                          <Button
                            variant="primary"
                            onClick={() => setShowSubscriptionModal(true)}
                            className="w-full sm:w-auto"
                          >
                            <Crown className="h-5 w-5 mr-2" />
                            Passer à Premium
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
                  {productsLoading ? (
                    // Loading skeleton
                    Array.from({ length: 4 }).map((_, index) => (
                      <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                        <div className="aspect-square bg-gray-200"></div>
                        <div className="p-4">
                          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
                          <div className="h-5 bg-gray-200 rounded w-1/4 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-full mt-4 pt-4"></div>
                        </div>
                      </div>
                    ))
                  ) : products.length === 0 ? (
                    <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
                      <Package className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun produit</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Commencez par ajouter votre premier produit
                      </p>
                      {canAddProduct() && (
                        <Button
                          variant="primary"
                          onClick={() => {
                            setSelectedProduct(null)
                            setShowProductModal(true)
                          }}
                          className="mt-4"
                        >
                          <Plus className="h-5 w-5 mr-2" />
                          Ajouter un Produit
                        </Button>
                      )}
                    </div>
                  ) : (
                    products.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onEdit={handleEditProduct}
                        onDelete={handleDeleteProduct}
                      />
                    ))
                  )}
                </div>

                {/* Subscription Upgrade Banner */}
                {sellerStore?.subscription_type !== 'premium' && products.length > 0 && products.length >= PRODUCT_LIMITS.free / 2 && (
                  <div className="mt-8 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg p-4 sm:p-6 shadow-lg">
                    <div className="flex flex-col sm:flex-row items-center justify-between">
                      <div className="mb-4 sm:mb-0">
                        <div className="flex items-center">
                          <Crown className="h-6 w-6 text-yellow-300 mr-2" />
                          <h3 className="text-lg font-bold">Passez à l'abonnement Premium</h3>
                        </div>
                        <p className="mt-1 text-white/80">
                          Vous avez utilisé {products.length}/{PRODUCT_LIMITS.free} produits. Passez à Premium pour publier jusqu'à {PRODUCT_LIMITS.premium} produits.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="text-white border-white hover:bg-white/20 w-full sm:w-auto"
                        onClick={() => setShowSubscriptionModal(true)}
                      >
                        <Crown className="h-5 w-5 mr-2 text-yellow-300" />
                        Passer à Premium
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'delivery' && (
              <div>
                <div className="bg-white shadow rounded-lg overflow-hidden">
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                      Livraisons
                    </h3>
                  </div>
                  <div className="p-4">
                    {deliveryRequests.length === 0 ? (
                      <div className="text-center py-12">
                        <Truck className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune livraison</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Vous n'avez pas encore de livraisons en cours
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Commande
                              </th>
                              <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Client
                              </th>
                              <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Livreur
                              </th>
                              <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Étape
                              </th>
                              <th scope="col" className="relative px-4 sm:px-6 py-3">
                                <span className="sr-only">Actions</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {deliveryRequests.map((request) => (
                              <tr key={request.id}>
                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="h-10 w-10 flex-shrink-0">
                                      <Package className="h-10 w-10 text-gray-400" />
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900 line-clamp-1">
                                        {request.order.product.title}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        #{request.order_id.slice(0, 8)}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">
                                    {request.order.buyer?.full_name || 'Client'}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {request.order.buyer?.phone_number}
                                  </div>
                                </td>
                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                  {request.delivery_partner ? (
                                    <div className="flex items-center">
                                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                        <User className="h-4 w-4 text-gray-500" />
                                      </div>
                                      <div className="ml-3">
                                        <div className="text-sm font-medium text-gray-900">
                                          {request.delivery_partner.applicant_name}
                                        </div>
                                        <div className="text-sm text-gray-500 line-clamp-1">
                                          {request.delivery_partner.applicant_email}
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-500">Non assigné</span>
                                  )}
                                </td>
                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    request.current_step === 'pending'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : request.current_step === 'delivered'
                                      ? 'bg-green-100 text-green-800'
                                      : request.current_step === 'cancelled'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {DELIVERY_STEPS.find(s => s.value === request.current_step)?.label}
                                  </span>
                                </td>
                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedDelivery(request)
                                      setShowDeliveryDetails(true)
                                    }}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    Détails
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'orders' && <SellerOrdersPage />}
            {activeTab === 'revenue' && <SellerRevenuePage />}
            {activeTab === 'payments' && <SellerPaymentHistory />}
            {activeTab === 'videos' && (
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Vidéos</h2>
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Video className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune vidéo</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Vous n'avez pas encore téléchargé de vidéos
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/videos/new')}
                    className="mt-4"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Télécharger une vidéo
                  </Button>
                </div>
              </div>
            )}
            {activeTab === 'loans' && (
              <div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    Financement et Prêts
                  </h2>
                  <Button
                    variant="primary"
                    onClick={() => setShowLoanRequestModal(true)}
                    className="w-full sm:w-auto"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Demander un Prêt
                  </Button>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Info className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Programme de Financement pour Vendeurs
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>
                          Notre programme de financement vous permet d'obtenir des fonds pour développer votre activité, 
                          augmenter votre inventaire ou améliorer votre logistique. Les prêts sont remboursés par un 
                          pourcentage de vos ventes futures, ce qui facilite la gestion de votre trésorerie.
                        </p>
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <p className="text-xs font-medium text-blue-800">Montants disponibles</p>
                          <p className="text-sm text-blue-700">$100 - $10,000</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <p className="text-xs font-medium text-blue-800">Durée de remboursement</p>
                          <p className="text-sm text-blue-700">3 à 12 mois</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <p className="text-xs font-medium text-blue-800">Frais de service</p>
                          <p className="text-sm text-blue-700">5% - 15%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <LoanHistory />
              </div>
            )}
            {activeTab === 'settings' && user && (
              <PaymentSettings sellerId={user.id} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export { SellerDashboard }